import { z } from 'zod';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [LIFECYCLE] THE ARCHIVE AXIS — AND THE RULE THAT SAYS WHICH VOCABULARY AN ENTITY TAKES.
 *
 *  ⚠️ ARCHIVE IS AN AXIS, NOT A STATUS. No status enum in this repository contains
 *  `ARCHIVED`, and none may gain one. `status` answers "where is this in its own life";
 *  `archived_at` answers "is it in circulation". A status member would have forced every
 *  existing status query to be re-read; a second column left them all working.
 *
 *  ── Which vocabulary does a new entity take? ──
 *
 *  `archivedAt` is for CONTENT — something a frozen policy version, an issued order or a
 *  duty can point at for ever. Withdrawing one is a RETENTION decision: it has a time, an
 *  actor, a restore, and possibly a purge.
 *
 *  `isActive` is for the ESTABLISHMENT and the GAZETTEER — a person, a rank, an org node,
 *  a place. The flag answers "does this exist in the force / may it be chosen today". It
 *  has no restore-to-prior-state and no purge, and on `Officer` it is an authentication
 *  gate that revokes sessions.
 *
 *  The mechanical tie-breaker: if "when, by whom, and may it be destroyed in thirty days?"
 *  is a question anyone will ask, it is `archivedAt`. If it never is, it is `isActive`.
 *
 *  Applied: Arrangement · StandingOrder · Policy · PostType · VehicleType take this axis.
 *  Officer · Rank · OrgUnit · Place keep `is_active`.
 *
 *  ⚠️ PLACE IS THE ONE THAT WILL BE QUERIED NEXT, so answer it here. A place is
 *  deactivated because it was WRONG or was merged away — `place.service.ts` deliberately
 *  offers no reactivate, its recovery is "create it again", and it already carries
 *  `merged_into_id` as its own tombstone axis. It is a gazetteer row, not content.
 *
 *  ⚠️ NO ENTITY CARRIES BOTH. `api/tests/integration/flagVocabulary.test.ts` reads every
 *  model and every schema and fails if one names `is_active` and `archived_at` together.
 *  Prose in a header cannot hold that; a test can.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ WHAT `archivedFromStatus` MEANS FOR AN ENTITY WITH NO STATUS COLUMN.
 *
 * For an entity that has one — Arrangement, StandingOrder — it is literally the status
 * held at the moment of archiving, and restore writes it back. For a post type, a vehicle
 * type or a policy there is no status column, and inventing one would produce a machine
 * with exactly two reachable states that nothing else in the system ever asks about. So
 * the column stores those two states directly, derived at archive time from the one
 * MONOTONIC fact that separates them:
 *
 *     has this row ever been part of a PUBLISHED policy version?
 *
 * Monotonic is what makes freezing the answer safe. A published version never
 * un-publishes (`policy.service.ts`'s `deletePolicyVersion` refuses one), so a value
 * computed once can never become stale.
 *
 * ⚠️ AND THE HONEST DISCREPANCY, STATED HERE RATHER THAN DISCOVERED: restore for a master
 * CLEARS these columns and writes nothing back, because there is nothing to write it to.
 * The name promises a write-back that only two of the five entities perform. One name
 * across five entities is worth more than five accurate ones — but only if the difference
 * is written down, and this is where.
 */
export const MASTER_LIFECYCLE_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export type MasterLifecycleStatus = (typeof MASTER_LIFECYCLE_STATUSES)[number];

/**
 * The three time/actor columns, identical on all five entities.
 *
 * ⚠️ `archivedFromStatus` IS NOT HERE. It is typed PER ENTITY, so that restore is a
 * type-checked assignment — `doc.status = doc.archived_from_status` compiles for an
 * Arrangement and would not compile against a union of all five vocabularies. A shared
 * union needs a cast at every restore site, and a cast is where "no lookup table" quietly
 * becomes a lookup table.
 */
export const archivableFieldsSchema = z.object({
  archivedAt: z.iso.datetime().nullable(),
  archivedByOfficerId: objectIdSchema.nullable(),
  /**
   * ⚠️ `null` ON ANYTHING THAT WILL NEVER BE DESTROYED, AND THAT IS THE RETENTION DECISION
   * ITSELF RATHER THAN A CONSEQUENCE OF ONE.
   *
   * §7 purges archived DRAFTS only; an archived PUBLISHED record is the record of an order
   * issued and then cancelled, and is kept for ever. Writing a date on one would be a
   * second answer to "will this be destroyed?", and the two disagree in exactly the case
   * that matters.
   *
   * The window is frozen at archive time rather than recomputed from `archivedAt`, on the
   * argument `fill_opens_at` already carries: changing the retention period must move only
   * records not yet archived, never shorten the clock on something already in the Archive.
   */
  purgeAt: z.iso.datetime().nullable(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §8] A LIVE DRAFT IS DELETED THIS MANY DAYS AFTER `draft_since`, EDITED OR NOT.
 *
 *  The client's rule: "a draft older than 30 days FROM CREATION is deleted, regardless of when
 *  it was last updated" — so a draft edited yesterday and started thirty-one days ago goes, and
 *  that is deliberate rather than a defect somebody will one day "fix" by keying on `updatedAt`.
 *  `draft_since` is creation for a never-published draft and the un-publish for an order taken
 *  back to draft (`arrangement.model.ts` names every writer).
 *
 *  ⚠️ A SEPARATE CONSTANT FROM THE ARCHIVE WINDOW (`PURGE_AFTER_DAYS` in the api), THOUGH BOTH ARE
 *  THIRTY TODAY. They are two clocks on two sets — the archive window is FROZEN onto an archived
 *  draft's `purge_at`, this one measures a LIVE draft's age — and one constant would move both the
 *  day somebody means to move one.
 *
 *  ⚠️ THIRTY × 24 HOURS, NEVER THIRTY IST DAYS. `draft_since` and `now` are instants; a draft
 *  started at 10:00 goes at the first sweep after 10:00 thirty days later.
 *
 *  In `@pams/shared` because the builder's expiry notice reads it: one number on the screen and in
 *  the selector, rather than a `30` typed twice.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const DRAFT_MAX_AGE_DAYS = 30;

/**
 * ⚠️ [C5c §8] WHICH DOOR DESTROYED A DRAFT — the `basis` on its `ARRANGEMENT_PURGED` audit row.
 *
 * One action for all three, because they have one outcome (`auditLog.schema.ts` says why two names
 * for one outcome is how an audit read path stops being trustworthy). `MANUAL` is a person's
 * `DELETE /arrangements/:id`; the other two are the sweep's two rules. A tuple here so the audit
 * page's words are a `Record` over it and a fourth door is a compile error there, not a raw code.
 */
export const DRAFT_PURGE_BASES = ['MANUAL', 'ARCHIVE_EXPIRED', 'DRAFT_AGE'] as const;
export type DraftPurgeBasis = (typeof DRAFT_PURGE_BASES)[number];

/**
 * The list axis, on every archivable entity's list query.
 *
 * ⚠️ THREE VALUES, WHICH IS WHY IT IS NOT A BOOLEAN. `archived: z.stringbool()` (which
 * `listArrangementsQuerySchema` carried) can express "current" and "archived only"; it
 * cannot express "all". `includeInactive: z.stringbool()` (which the three masters
 * carried) can express "current" and "all"; it cannot express "archived only" — which is
 * the view an operator needs to find the row they just archived and restore it.
 *
 * ⚠️ AND `includeInactive=true` MEANT SOMETHING DIFFERENT FROM `show=archived`. It WIDENED
 * the list to both; this SWITCHES it. That is the correct shape — the masters get the same
 * Archive view the arrangement already had, and one index serves all three values — but it
 * is a semantic change wearing a rename, and the CHANGELOG says so.
 */
export const SHOW_LIFECYCLE_VALUES = ['current', 'archived', 'all'] as const;
export type ShowLifecycle = (typeof SHOW_LIFECYCLE_VALUES)[number];

export const showLifecycleSchema = z.enum(SHOW_LIFECYCLE_VALUES).default('current');
